import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    Popover,
    Select,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import AccountTreeOutlinedIcon from '@material-ui/icons/AccountTreeOutlined'
import AddIcon from '@material-ui/icons/Add'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import BuildOutlinedIcon from '@material-ui/icons/BuildOutlined'
import CloseIcon from '@material-ui/icons/Close'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import SearchIcon from '@material-ui/icons/Search'
import SwapHorizIcon from '@material-ui/icons/SwapHoriz'

import { useAptConfig } from '../../hooks/useAptConfig'
import { usePremiums } from '../../hooks/usePremiums'
import type { CustomFieldInputType } from '../ApplicationWorkbook/workbook.types'
import {
    CUSTOM_QUESTION_INPUT_TYPES,
    FieldShowIf,
    WorkbookBucketId,
    WorkbookSectionDraft,
    WorkbookSectionFieldDraft,
    WorkbookSectionsState,
    answerChoicesForTrigger,
    buildSectionFieldDisplayRows,
    canBeContingencyTrigger,
    clearContingenciesOnRemovedTrigger,
    contingencyTooltipBody,
    createSectionDraft,
    defaultOptionsForField,
    displayAnswerForShowIf,
    getWorkbookSectionDraft,
    inputTypeLabel,
    inputTypeNeedsOptions,
    LIVE_PROPERTY_CHOICE_FIELD_IDS,
    listWorkbookSections,
    moveFieldAmongSiblings,
    placeFieldAfterParent,
    siblingMoveFlags,
    storedAnswerForShowIf,
    workbookBucketLabel,
} from './workbookSectionDraft'
import {
    FieldIdCollision,
    FieldRegistryAdapter,
    OrgWorkbookField,
    defaultOrgFieldRegistryAdapter,
    orgFieldToSectionDraft,
} from './orgFieldRegistry'
import { WorkbookFieldPreview, PetPreviewOption } from './WorkbookFieldPreview'
import { FhaAckDialog } from '../ApplicationWorkbook/FhaAckDialog'
import {
    GENDER_FIELD_ID,
    GenderQuestionMode,
    genderQuestionSpec,
    normalizeGenderQuestionMode,
} from '../ApplicationWorkbook/genderQuestion'
import { workbookFieldTitle } from '../ApplicationWorkbook/workbookCatalog'

type AddMode = 'closed' | 'chooser' | 'custom' | 'existing'

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        width: '80vw',
        maxWidth: '80vw',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    titleIcon: {
        color: theme.palette.text.secondary,
        fontSize: 22,
    },
    titleText: {
        fontWeight: 700,
        fontSize: '1.25rem',
        lineHeight: 1.3,
    },
    titleSummary: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.45,
        marginTop: theme.spacing(0.75),
        fontWeight: 400,
    },
    sectionLabelRow: {
        marginBottom: theme.spacing(1.5),
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
    },
    fieldList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    fieldRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
    },
    fieldRowNested: {
        marginLeft: theme.spacing(3),
    },
    numberRail: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 2,
        minWidth: 40,
        flexShrink: 0,
        paddingTop: theme.spacing(1.25),
    },
    orderNumber: {
        fontSize: '1.25rem',
        fontWeight: 700,
        color: theme.palette.text.secondary,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
    },
    contingencyInfoBtn: {
        padding: 2,
        color: theme.palette.text.secondary,
        '& svg': {
            fontSize: 16,
        },
    },
    contingencyTooltip: {
        maxWidth: 360,
        padding: theme.spacing(1.5, 1.75),
    },
    contingencyTooltipTitle: {
        color: '#fff',
        fontWeight: 700,
        fontSize: '0.9375rem',
        lineHeight: 1.3,
        marginBottom: 6,
    },
    contingencyTooltipBody: {
        color: '#fff',
        fontSize: '0.8125rem',
        fontWeight: 400,
        lineHeight: 1.45,
    },
    railBtn: {
        padding: 2,
    },
    inlineActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        flexShrink: 0,
        marginLeft: 'auto',
    },
    fieldCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: theme.spacing(1.25, 1.5, 1.5),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        width: '100%',
        flex: 1,
        minWidth: 0,
    },
    cardColumns: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    editorColumn: {
        flex: '1 1 300px',
        maxWidth: 400,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
        [theme.breakpoints.down('sm')]: {
            maxWidth: 'none',
            width: '100%',
        },
    },
    editorHeader: {
        display: 'flex',
        alignItems: 'baseline',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    cardTitle: {
        fontSize: '0.9375rem',
        fontWeight: 700,
        lineHeight: 1.3,
        minWidth: 0,
    },
    previewColumn: {
        flex: '1.4 1 340px',
        minWidth: 0,
        alignSelf: 'stretch',
        backgroundColor: '#f3f4f6',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: theme.spacing(1.25, 1.5, 1.5),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.background.paper,
        },
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    previewLabel: {
        fontSize: '0.8125rem',
        fontWeight: 700,
        lineHeight: 1.3,
        color: theme.palette.text.secondary,
    },
    cardToolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minHeight: 32,
        flexWrap: 'wrap',
    },
    pinnedHint: {
        fontSize: '0.75rem',
        color: theme.palette.text.disabled,
        lineHeight: 1.3,
        flexShrink: 0,
    },
    mutedMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
        flexWrap: 'wrap',
    },
    fieldIdMuted: {
        fontSize: '0.75rem',
        fontWeight: 500,
        lineHeight: 1.3,
        color: theme.palette.text.disabled,
        fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        minWidth: 0,
        wordBreak: 'break-all',
    },
    fieldIdEdit: {
        maxWidth: 220,
        '& .MuiOutlinedInput-input': {
            fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: '0.75rem',
            padding: theme.spacing(0.75, 1),
            color: theme.palette.text.secondary,
        },
    },
    typeChip: {
        height: 22,
        fontSize: '0.75rem',
        fontWeight: 600,
    },
    contingencyPopover: {
        padding: theme.spacing(2),
        width: 320,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
    },
    contingencyTitle: {
        fontWeight: 700,
        fontSize: '0.875rem',
    },
    fieldCardDraft: {
        borderStyle: 'dashed',
        backgroundColor: theme.palette.action.hover,
        gap: theme.spacing(1),
        padding: theme.spacing(1.25, 1.5),
        maxWidth: 700,
    },
    questionText: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.35,
    },
    typeControl: {
        minWidth: 120,
        maxWidth: 160,
    },
    requiredToggle: {
        flexShrink: 0,
        '& .MuiToggleButton-root': {
            padding: theme.spacing(0.35, 1),
            fontSize: '0.75rem',
            lineHeight: 1.2,
            textTransform: 'none',
        },
    },
    deleteBtn: {
        color: theme.palette.error.main,
        padding: 4,
    },
    /** Used in Add-question draft form rows. */
    fieldIdLine: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
        flexWrap: 'wrap',
    },
    actionsRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    chooserRow: {
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    chooserBtn: {
        textTransform: 'none',
        fontWeight: 600,
    },
    draftHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
    },
    draftTitle: {
        fontWeight: 700,
        fontSize: '0.8125rem',
    },
    suggestedId: {
        fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        fontSize: '0.8125rem',
    },
    hint: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.4,
    },
    collision: {
        padding: theme.spacing(1.25, 1.5),
        borderRadius: 8,
        border: `1px solid ${theme.palette.warning.main}55`,
        backgroundColor: theme.palette.warning.main + '12',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    collisionTitle: {
        fontWeight: 700,
        fontSize: '0.8125rem',
    },
    collisionMeta: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    collisionActions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    existingList: {
        maxHeight: 240,
        overflow: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        marginTop: theme.spacing(1),
    },
    existingItem: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.25),
        borderBottom: `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        '&:last-child': {
            borderBottom: 'none',
        },
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    existingMeta: {
        fontSize: '0.6875rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    emptyList: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        padding: theme.spacing(2),
        textAlign: 'center',
    },
}))

function textToOptions(value: string): string[] {
    return value
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
}

export function ConfigureWorkbookSectionModal({
    open,
    bucketId,
    sections,
    initialDraft,
    onClose,
    onSave,
    onMoveField,
    fieldRegistry = defaultOrgFieldRegistryAdapter,
    editSectionMeta = false,
    editCatalogFields = false,
    policyLockedFieldIds = [],
    animalTypeOptions = [],
    dogBreedOptions = [],
}: {
    open: boolean
    bucketId: WorkbookBucketId | null
    /** Full workbook state — used for move-target labels. */
    sections?: WorkbookSectionsState
    initialDraft?: WorkbookSectionDraft | null
    onClose: () => void
    onSave: (draft: WorkbookSectionDraft) => void
    /** Move a field out of this section into another; parent updates state. */
    onMoveField?: (
        field: WorkbookSectionFieldDraft,
        toSectionId: WorkbookBucketId,
        fromDraftWithoutField: WorkbookSectionDraft,
    ) => void
    /** Defaults to in-memory org demo registry. */
    fieldRegistry?: FieldRegistryAdapter
    /** When true, section name + summary are editable for platform and custom. */
    editSectionMeta?: boolean
    /**
     * EZOS admin template editor: unlock field ID, question text, and response
     * type for catalog fields (org leasing-season config keeps them locked).
     */
    editCatalogFields?: boolean
    /**
     * Field IDs owned by Qualifying Methods (or similar): semantics, options,
     * required, delete, and move-to-section stay locked.
     */
    policyLockedFieldIds?: string[]
    /** Live Property Data animal types for the pet-type preview. */
    animalTypeOptions?: PetPreviewOption[]
    /** Live Property Data dog breeds for the pet-breed preview. */
    dogBreedOptions?: PetPreviewOption[]
}) {
    const classes = useStyles()
    const { unitConfigList } = useAptConfig({ unitConfigList: true })
    const { premiums } = usePremiums()
    const floorPlanOptions = useMemo(
        () =>
            (unitConfigList || [])
                .filter((config) => config.shows_on_schedule)
                .map((config) => ({
                    id: config.id,
                    name: config.name,
                    bed_count:
                        (config as { bed_count?: number | null }).bed_count ??
                        null,
                    bath_count:
                        (config as { bath_count?: number | null }).bath_count ??
                        null,
                })),
        [unitConfigList],
    )
    const premiumOptions = useMemo(
        () =>
            (premiums || [])
                .filter((premium) => premium.is_active)
                .map((premium) => ({
                    id: premium.id,
                    name: premium.name,
                    description: premium.description,
                    amount: String(premium.amount),
                })),
        [premiums],
    )
    const registry = fieldRegistry
    const policyLockedSet = useMemo(() => new Set(policyLockedFieldIds), [
        policyLockedFieldIds,
    ])
    const [draft, setDraft] = useState<WorkbookSectionDraft | null>(null)
    const [addMode, setAddMode] = useState<AddMode>('closed')
    const [newQuestion, setNewQuestion] = useState('')
    const [newInputType, setNewInputType] = useState<CustomFieldInputType>(
        'text',
    )
    const [newOptions, setNewOptions] = useState('Option 1\nOption 2')
    const [suggestedId, setSuggestedId] = useState('')
    /** Once the user edits the field ID, stop overwriting it from question text. */
    const [fieldIdManual, setFieldIdManual] = useState(false)
    const [collision, setCollision] = useState<FieldIdCollision | null>(null)
    const [existingQuery, setExistingQuery] = useState('')
    const [
        pendingGenderField,
        setPendingGenderField,
    ] = useState<OrgWorkbookField | null>(null)
    const [moveMenuAnchor, setMoveMenuAnchor] = useState<{
        el: HTMLElement
        fieldId: string
    } | null>(null)
    const [contingencyAnchor, setContingencyAnchor] = useState<{
        el: HTMLElement
        fieldId: string
    } | null>(null)
    const [contingencyDependsOn, setContingencyDependsOn] = useState('')
    const [contingencyEquals, setContingencyEquals] = useState('')

    const resetAddForm = () => {
        setNewQuestion('')
        setNewInputType('text')
        setNewOptions('Option 1\nOption 2')
        setSuggestedId('')
        setFieldIdManual(false)
        setCollision(null)
        setExistingQuery('')
    }

    const sanitizeFieldIdInput = (raw: string) =>
        raw
            .toLowerCase()
            .replace(/[^a-z0-9_]/g, '_')
            .replace(/_+/g, '_')
            .slice(0, 64)

    const closeAdd = () => {
        setAddMode('closed')
        resetAddForm()
    }

    useEffect(() => {
        if (!open || !bucketId) {
            setDraft(null)
            return
        }
        const source = initialDraft
            ? {
                  ...initialDraft,
                  label: initialDraft.label,
                  kind: initialDraft.kind,
                  // Presence = included; drop previously "unchecked" fields.
                  fields: initialDraft.fields
                      .filter((field) => field.enabled !== false)
                      .map((field) => ({
                          ...field,
                          enabled: true,
                          options: field.options
                              ? [...field.options]
                              : undefined,
                      })),
              }
            : createSectionDraft(bucketId, false)
        setDraft(source)
        setAddMode('closed')
        setNewQuestion('')
        setNewInputType('text')
        setNewOptions('Option 1\nOption 2')
        setSuggestedId('')
        setCollision(null)
        setExistingQuery('')
        setMoveMenuAnchor(null)
        setContingencyAnchor(null)
        // Only re-seed when the modal opens for a section — not on every parent render.
    }, [open, bucketId])

    const moveTargets = useMemo(() => {
        if (!bucketId) return []
        return listWorkbookSections(sections).filter(
            (s) => s.bucket_id !== bucketId,
        )
    }, [sections, bucketId])

    const displayRows = useMemo(
        () => buildSectionFieldDisplayRows(draft?.fields ?? []),
        [draft?.fields],
    )

    const placedIds = useMemo(
        () => new Set(draft?.fields.map((f) => f.field_id) ?? []),
        [draft],
    )

    const existingCandidates = useMemo(() => {
        const q = existingQuery.trim().toLowerCase()
        return registry
            .listFields()
            .filter((f) => !placedIds.has(f.field_id))
            .filter((f) => {
                if (!q) return true
                return (
                    f.field_id.toLowerCase().includes(q) ||
                    f.question_text.toLowerCase().includes(q) ||
                    f.input_type.toLowerCase().includes(q)
                )
            })
            .sort((a, b) => {
                if (a.source !== b.source) {
                    return a.source === 'ezos_catalog' ? -1 : 1
                }
                return a.field_id.localeCompare(b.field_id)
            })
    }, [existingQuery, placedIds, registry])

    const refreshSuggestion = (question: string, force = false) => {
        if (fieldIdManual && !force) {
            return
        }
        const next = question.trim() ? registry.suggestFieldId(question) : ''
        setSuggestedId(next)
        if (!next) {
            setCollision(null)
            return
        }
        setCollision(registry.checkCollision(next))
    }

    const applyFieldIdEdit = (raw: string) => {
        setFieldIdManual(true)
        const next = sanitizeFieldIdInput(raw)
        setSuggestedId(next)
        if (!next) {
            setCollision(null)
            return
        }
        setCollision(registry.checkCollision(next))
    }

    const sectionAlreadyHas = (fieldId: string) => placedIds.has(fieldId)

    const appendField = (field: WorkbookSectionFieldDraft) => {
        setDraft((prev) => {
            if (!prev) return prev
            if (prev.fields.some((f) => f.field_id === field.field_id)) {
                return prev
            }
            return {
                ...prev,
                fields: [...prev.fields, { ...field, enabled: true }],
            }
        })
        closeAdd()
    }

    if (!bucketId || !draft) {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={false}
                fullWidth
                PaperProps={{ className: classes.paper }}
            >
                <DialogTitle>
                    <Box className={classes.titleRow}>
                        <BuildOutlinedIcon className={classes.titleIcon} />
                        <span className={classes.titleText}>Section</span>
                    </Box>
                </DialogTitle>
                <DialogContent />
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                </DialogActions>
            </Dialog>
        )
    }

    const title = draft.label || workbookBucketLabel(bucketId, sections)

    const updateField = (
        fieldId: string,
        patch: Partial<WorkbookSectionFieldDraft>,
    ) => {
        setDraft((prev) => {
            if (!prev) return prev
            return {
                ...prev,
                fields: prev.fields.map((field) =>
                    field.field_id === fieldId ? { ...field, ...patch } : field,
                ),
            }
        })
    }

    const removeField = (fieldId: string) => {
        if (policyLockedSet.has(fieldId)) return
        const target = draft?.fields.find((f) => f.field_id === fieldId)
        if (target?.pinned) return
        setDraft((prev) => {
            if (!prev) return prev
            const remaining = prev.fields.filter((f) => f.field_id !== fieldId)
            return {
                ...prev,
                fields: clearContingenciesOnRemovedTrigger(remaining, fieldId),
            }
        })
    }

    const handleReorder = (fieldId: string, direction: 'up' | 'down') => {
        setDraft((prev) => {
            if (!prev) return prev
            return {
                ...prev,
                fields: moveFieldAmongSiblings(prev.fields, fieldId, direction),
            }
        })
    }

    const openContingencyEditor = (el: HTMLElement, fieldId: string) => {
        const field = draft?.fields.find((f) => f.field_id === fieldId)
        if (!field || field.pinned || policyLockedSet.has(fieldId)) return
        const dependsOn = field.show_if?.depends_on_field_id ?? ''
        const parent = draft?.fields.find((f) => f.field_id === dependsOn)
        setContingencyDependsOn(dependsOn)
        setContingencyEquals(
            storedAnswerForShowIf(parent, field.show_if?.equals ?? ''),
        )
        setContingencyAnchor({ el, fieldId })
    }

    const saveContingency = () => {
        if (!contingencyAnchor || !draft) return
        const fieldId = contingencyAnchor.fieldId
        const dependsOn = contingencyDependsOn
        const equals = contingencyEquals
        if (!dependsOn || !equals) return
        if (dependsOn === fieldId) return

        const trigger = draft.fields.find((f) => f.field_id === dependsOn)
        const show_if: FieldShowIf = {
            depends_on_field_id: dependsOn,
            equals: storedAnswerForShowIf(trigger, equals),
        }
        setDraft((prev) => {
            if (!prev) return prev
            const updated = prev.fields.map((f) =>
                f.field_id === fieldId ? { ...f, show_if } : f,
            )
            return {
                ...prev,
                fields: placeFieldAfterParent(updated, fieldId, dependsOn),
            }
        })
        setContingencyAnchor(null)
    }

    const clearContingency = () => {
        if (!contingencyAnchor) return
        const fieldId = contingencyAnchor.fieldId
        setDraft((prev) => {
            if (!prev) return prev
            return {
                ...prev,
                fields: prev.fields.map((field) => {
                    if (field.field_id !== fieldId) return field
                    const { show_if: _removed, ...rest } = field
                    return rest
                }),
            }
        })
        setContingencyAnchor(null)
    }

    const handleAddNewRegistryField = async (fieldId: string) => {
        const text = newQuestion.trim()
        if (!text) return
        const options = inputTypeNeedsOptions(newInputType)
            ? textToOptions(newOptions)
            : undefined
        if (inputTypeNeedsOptions(newInputType) && (options?.length ?? 0) < 2) {
            return
        }
        if (sectionAlreadyHas(fieldId)) {
            closeAdd()
            return
        }
        const registered = await Promise.resolve(
            registry.registerField({
                field_id: fieldId,
                question_text: text,
                input_type: newInputType,
                options,
                usedIn: [`This workbook · ${title}`],
                default_section_id: String(bucketId || ''),
            }),
        )
        appendField(orgFieldToSectionDraft(registered, { enabled: true }))
    }

    const handleTryAdd = () => {
        const text = newQuestion.trim()
        if (!text) return
        const id = suggestedId || registry.suggestFieldId(text)
        setSuggestedId(id)
        const hit = registry.checkCollision(id)
        if (hit) {
            setCollision(hit)
            return
        }
        void handleAddNewRegistryField(id)
    }

    const handleReuseCollision = () => {
        if (!collision) return
        if (sectionAlreadyHas(collision.field_id)) {
            closeAdd()
            return
        }
        appendField(
            orgFieldToSectionDraft(collision.existing, { enabled: true }),
        )
    }

    const handleDifferentQuestion = () => {
        if (!collision) return
        const text = newQuestion.trim()
        if (!text) return
        const alt = registry.suggestAlternativeFieldId(collision.field_id, text)
        const unique = registry.ensureUniqueFieldId(alt)
        setCollision(null)
        setFieldIdManual(true)
        setSuggestedId(unique)
        void handleAddNewRegistryField(unique)
    }

    const handlePickExisting = (field: OrgWorkbookField) => {
        if (field.field_id === GENDER_FIELD_ID) {
            setPendingGenderField(field)
            return
        }
        appendField(orgFieldToSectionDraft(field, { enabled: true }))
    }

    const handleMoveField = (fieldId: string, toId: string) => {
        if (!onMoveField || !draft) return
        const field = draft.fields.find((f) => f.field_id === fieldId)
        if (!field || field.pinned) return
        const moved: WorkbookSectionFieldDraft = {
            ...field,
            show_if: undefined,
        }
        const remaining = draft.fields.filter((f) => f.field_id !== fieldId)
        const fromDraftWithoutField: WorkbookSectionDraft = {
            ...draft,
            fields: clearContingenciesOnRemovedTrigger(remaining, fieldId),
        }
        setDraft(fromDraftWithoutField)
        onMoveField(moved, toId, fromDraftWithoutField)
    }

    const handleCustomTypeChange = (
        fieldId: string,
        inputType: CustomFieldInputType,
    ) => {
        const options = inputTypeNeedsOptions(inputType)
            ? defaultOptionsForField(fieldId, inputType)
            : undefined
        updateField(fieldId, {
            input_type: inputType,
            options,
        })
    }

    const renameFieldId = (oldId: string, nextId: string) => {
        const trimmed = nextId.trim()
        if (!trimmed || trimmed === oldId) return
        setDraft((prev) => {
            if (!prev) return prev
            if (prev.fields.some((f) => f.field_id === trimmed)) return prev
            return {
                ...prev,
                fields: prev.fields.map((field) => {
                    if (field.field_id === oldId) {
                        return { ...field, field_id: trimmed }
                    }
                    if (field.show_if?.depends_on_field_id === oldId) {
                        return {
                            ...field,
                            show_if: {
                                ...field.show_if,
                                depends_on_field_id: trimmed,
                            },
                        }
                    }
                    return field
                }),
            }
        })
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={false}
                fullWidth
                PaperProps={{ className: classes.paper }}
            >
                <DialogTitle disableTypography>
                    <Box className={classes.titleRow}>
                        <BuildOutlinedIcon className={classes.titleIcon} />
                        <Typography
                            className={classes.titleText}
                            component="h2"
                        >
                            {title}
                        </Typography>
                    </Box>
                    {draft.summary?.trim() ? (
                        <Typography className={classes.titleSummary}>
                            {draft.summary.trim()}
                        </Typography>
                    ) : null}
                </DialogTitle>
                <DialogContent dividers>
                    {editSectionMeta || draft.kind === 'custom' ? (
                        <Box className={classes.sectionLabelRow}>
                            <TextField
                                label="Section name"
                                value={draft.label}
                                onChange={(e) =>
                                    setDraft((prev) =>
                                        prev
                                            ? { ...prev, label: e.target.value }
                                            : prev,
                                    )
                                }
                                fullWidth
                                size="small"
                                variant="outlined"
                                style={{ marginBottom: 12 }}
                            />
                            {editSectionMeta ? (
                                <TextField
                                    label="Summary"
                                    value={draft.summary || ''}
                                    onChange={(e) =>
                                        setDraft((prev) =>
                                            prev
                                                ? {
                                                      ...prev,
                                                      summary: e.target.value,
                                                  }
                                                : prev,
                                        )
                                    }
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    multiline
                                    rows={2}
                                />
                            ) : null}
                        </Box>
                    ) : null}

                    <Box className={classes.toolbar}>
                        <Button
                            color="primary"
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                resetAddForm()
                                setAddMode('chooser')
                            }}
                            disabled={addMode !== 'closed'}
                        >
                            Add question
                        </Button>
                    </Box>

                    {addMode !== 'closed' ? (
                        <Box
                            className={`${classes.fieldCard} ${classes.fieldCardDraft}`}
                            style={{ marginBottom: 8 }}
                        >
                            <Box className={classes.draftHeader}>
                                <Typography className={classes.draftTitle}>
                                    {addMode === 'chooser'
                                        ? 'Add question'
                                        : addMode === 'custom'
                                        ? 'Create custom question'
                                        : 'Add existing question'}
                                </Typography>
                                <IconButton
                                    size="small"
                                    aria-label="Cancel add"
                                    onClick={closeAdd}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>

                            {addMode === 'chooser' ? (
                                <>
                                    <Typography className={classes.hint}>
                                        Create a new org field, or pull one from
                                        the EZOS catalog / org registry.
                                    </Typography>
                                    <Box className={classes.chooserRow}>
                                        <Button
                                            className={classes.chooserBtn}
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => setAddMode('custom')}
                                        >
                                            Create custom
                                        </Button>
                                        <Button
                                            className={classes.chooserBtn}
                                            variant="outlined"
                                            onClick={() =>
                                                setAddMode('existing')
                                            }
                                        >
                                            Add existing
                                        </Button>
                                    </Box>
                                </>
                            ) : null}

                            {addMode === 'custom' ? (
                                <>
                                    <Typography className={classes.hint}>
                                        Type the question — we suggest a field
                                        ID you can edit. Collisions offer reuse
                                        of the existing field.
                                    </Typography>
                                    <TextField
                                        label="Question text"
                                        value={newQuestion}
                                        onChange={(e) => {
                                            const value = e.target.value
                                            setNewQuestion(value)
                                            refreshSuggestion(value)
                                        }}
                                        onBlur={() => {
                                            if (newQuestion.trim()) {
                                                refreshSuggestion(newQuestion)
                                            }
                                        }}
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        multiline
                                        rows={2}
                                        autoFocus
                                    />
                                    <TextField
                                        label="Field ID"
                                        value={suggestedId}
                                        onChange={(e) =>
                                            applyFieldIdEdit(e.target.value)
                                        }
                                        onBlur={() => {
                                            if (suggestedId.trim()) {
                                                setCollision(
                                                    registry.checkCollision(
                                                        suggestedId.trim(),
                                                    ),
                                                )
                                            }
                                        }}
                                        helperText={
                                            fieldIdManual
                                                ? 'Custom ID — letters, numbers, and underscores'
                                                : 'Suggested from the question — edit anytime'
                                        }
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        InputProps={{
                                            className: classes.suggestedId,
                                        }}
                                    />
                                    <Box className={classes.actionsRow}>
                                        <FormControl
                                            className={classes.typeControl}
                                            variant="outlined"
                                            size="small"
                                            disabled={!!collision}
                                        >
                                            <InputLabel id="new-input-type">
                                                Response type
                                            </InputLabel>
                                            <Select
                                                labelId="new-input-type"
                                                label="Response type"
                                                value={newInputType}
                                                onChange={(e) =>
                                                    setNewInputType(
                                                        e.target
                                                            .value as CustomFieldInputType,
                                                    )
                                                }
                                            >
                                                {CUSTOM_QUESTION_INPUT_TYPES.map(
                                                    (opt) => (
                                                        <MenuItem
                                                            key={opt.value}
                                                            value={opt.value}
                                                        >
                                                            {opt.label}
                                                        </MenuItem>
                                                    ),
                                                )}
                                            </Select>
                                        </FormControl>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            size="small"
                                            onClick={handleTryAdd}
                                            disabled={
                                                !newQuestion.trim() ||
                                                !suggestedId.trim() ||
                                                !!collision
                                            }
                                        >
                                            Add to section
                                        </Button>
                                    </Box>
                                    {inputTypeNeedsOptions(newInputType) &&
                                    !collision ? (
                                        <TextField
                                            label="Answer options (one per line)"
                                            value={newOptions}
                                            onChange={(e) =>
                                                setNewOptions(e.target.value)
                                            }
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            multiline
                                            rows={2}
                                        />
                                    ) : null}
                                    {collision ? (
                                        <Box className={classes.collision}>
                                            <Typography
                                                className={
                                                    classes.collisionTitle
                                                }
                                            >
                                                Field ID already used in your
                                                organization
                                            </Typography>
                                            <Typography
                                                className={classes.questionText}
                                            >
                                                {
                                                    collision.existing
                                                        .question_text
                                                }
                                            </Typography>
                                            <Typography
                                                className={
                                                    classes.collisionMeta
                                                }
                                            >
                                                ID{' '}
                                                <span
                                                    className={
                                                        classes.suggestedId
                                                    }
                                                >
                                                    {
                                                        collision.existing
                                                            .field_id
                                                    }
                                                </span>
                                                {' · '}
                                                type{' '}
                                                {inputTypeLabel(
                                                    collision.existing
                                                        .input_type,
                                                )}
                                                {collision.existing.usedIn &&
                                                collision.existing.usedIn
                                                    .length > 0
                                                    ? ` · used in ${collision.existing.usedIn.join(
                                                          ', ',
                                                      )}`
                                                    : ''}
                                            </Typography>
                                            <Typography
                                                className={classes.hint}
                                            >
                                                Is this the question you want to
                                                ask?
                                            </Typography>
                                            <Box
                                                className={
                                                    classes.collisionActions
                                                }
                                            >
                                                <Button
                                                    color="primary"
                                                    variant="contained"
                                                    size="small"
                                                    onClick={
                                                        handleReuseCollision
                                                    }
                                                    disabled={sectionAlreadyHas(
                                                        collision.field_id,
                                                    )}
                                                >
                                                    {sectionAlreadyHas(
                                                        collision.field_id,
                                                    )
                                                        ? 'Already in this section'
                                                        : 'Yes — use this question'}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={
                                                        handleDifferentQuestion
                                                    }
                                                >
                                                    No — different question
                                                </Button>
                                            </Box>
                                        </Box>
                                    ) : null}
                                </>
                            ) : null}

                            {addMode === 'existing' ? (
                                <>
                                    <TextField
                                        label="Search EZOS catalog & org registry"
                                        value={existingQuery}
                                        onChange={(e) =>
                                            setExistingQuery(e.target.value)
                                        }
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        autoFocus
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Box className={classes.existingList}>
                                        {existingCandidates.length === 0 ? (
                                            <Typography
                                                className={classes.emptyList}
                                            >
                                                No matching questions available
                                                to add.
                                            </Typography>
                                        ) : (
                                            existingCandidates.map((field) => (
                                                <Box
                                                    key={field.field_id}
                                                    className={
                                                        classes.existingItem
                                                    }
                                                    onClick={() =>
                                                        handlePickExisting(
                                                            field,
                                                        )
                                                    }
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                        if (
                                                            e.key === 'Enter' ||
                                                            e.key === ' '
                                                        ) {
                                                            e.preventDefault()
                                                            handlePickExisting(
                                                                field,
                                                            )
                                                        }
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography
                                                            className={
                                                                classes.questionText
                                                            }
                                                        >
                                                            {
                                                                field.question_text
                                                            }
                                                        </Typography>
                                                        <Typography
                                                            className={
                                                                classes.existingMeta
                                                            }
                                                        >
                                                            <span
                                                                className={
                                                                    classes.suggestedId
                                                                }
                                                            >
                                                                {field.field_id}
                                                            </span>
                                                            {' · '}
                                                            {field.source ===
                                                            'ezos_catalog'
                                                                ? 'EZOS'
                                                                : 'Org'}
                                                            {' · '}
                                                            {inputTypeLabel(
                                                                field.input_type,
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                    <Button
                                                        size="small"
                                                        color="primary"
                                                    >
                                                        Add
                                                    </Button>
                                                </Box>
                                            ))
                                        )}
                                    </Box>
                                </>
                            ) : null}
                        </Box>
                    ) : null}

                    <Box className={classes.fieldList}>
                        {displayRows.map((row) => {
                            const { field, number, depth } = row
                            const isCatalog = !field.is_custom
                            const policyLocked = policyLockedSet.has(
                                field.field_id,
                            )
                            const pinned = Boolean(field.pinned)
                            const semanticsLocked =
                                policyLocked ||
                                (isCatalog && !editCatalogFields)
                            const { canUp, canDown } = siblingMoveFlags(
                                draft.fields,
                                field.field_id,
                            )
                            const parentField = field.show_if
                                ? draft.fields.find(
                                      (f) =>
                                          f.field_id ===
                                          field.show_if!.depends_on_field_id,
                                  )
                                : undefined
                            const parentNumber = field.show_if
                                ? displayRows.find(
                                      (item) =>
                                          item.field.field_id ===
                                          field.show_if!.depends_on_field_id,
                                  )?.number
                                : undefined
                            const showCustomOptions =
                                !semanticsLocked &&
                                inputTypeNeedsOptions(field.input_type) &&
                                !LIVE_PROPERTY_CHOICE_FIELD_IDS.has(
                                    field.field_id,
                                )
                            return (
                                <Box
                                    key={
                                        field.source_field_id || field.field_id
                                    }
                                    className={`${classes.fieldRow}${
                                        depth > 0
                                            ? ` ${classes.fieldRowNested}`
                                            : ''
                                    }`}
                                >
                                    <Box className={classes.numberRail}>
                                        <Typography
                                            className={classes.orderNumber}
                                        >
                                            {number}
                                        </Typography>
                                        {field.show_if ? (
                                            <Tooltip
                                                arrow
                                                placement="right"
                                                classes={{
                                                    tooltip:
                                                        classes.contingencyTooltip,
                                                }}
                                                title={
                                                    <>
                                                        <Typography
                                                            className={
                                                                classes.contingencyTooltipTitle
                                                            }
                                                        >
                                                            Contingent question
                                                        </Typography>
                                                        <Typography
                                                            className={
                                                                classes.contingencyTooltipBody
                                                            }
                                                        >
                                                            {contingencyTooltipBody(
                                                                parentField,
                                                                field.show_if
                                                                    .equals,
                                                                parentNumber,
                                                            )}
                                                        </Typography>
                                                    </>
                                                }
                                            >
                                                <IconButton
                                                    className={
                                                        classes.contingencyInfoBtn
                                                    }
                                                    size="small"
                                                    aria-label="Contingent question"
                                                >
                                                    <InfoOutlinedIcon />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null}
                                    </Box>

                                    <Box className={classes.fieldCard}>
                                        <Box className={classes.cardColumns}>
                                            <Box
                                                className={classes.editorColumn}
                                            >
                                                <Box
                                                    className={
                                                        classes.editorHeader
                                                    }
                                                >
                                                    <Typography
                                                        className={
                                                            classes.cardTitle
                                                        }
                                                    >
                                                        {workbookFieldTitle(
                                                            field.field_id,
                                                        )}
                                                    </Typography>
                                                    {pinned ? (
                                                        <Typography
                                                            className={
                                                                classes.pinnedHint
                                                            }
                                                        >
                                                            Pinned
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                                <Box
                                                    className={
                                                        classes.cardToolbar
                                                    }
                                                >
                                                    <ToggleButtonGroup
                                                        className={
                                                            classes.requiredToggle
                                                        }
                                                        size="small"
                                                        exclusive
                                                        value={
                                                            field.required
                                                                ? 'required'
                                                                : 'optional'
                                                        }
                                                        onChange={(
                                                            _,
                                                            value,
                                                        ) => {
                                                            if (
                                                                !value ||
                                                                policyLocked
                                                            )
                                                                return
                                                            updateField(
                                                                field.field_id,
                                                                {
                                                                    required:
                                                                        value ===
                                                                        'required',
                                                                },
                                                            )
                                                        }}
                                                    >
                                                        <ToggleButton
                                                            value="optional"
                                                            disabled={
                                                                policyLocked
                                                            }
                                                        >
                                                            Optional
                                                        </ToggleButton>
                                                        <ToggleButton
                                                            value="required"
                                                            disabled={
                                                                policyLocked
                                                            }
                                                        >
                                                            Required
                                                        </ToggleButton>
                                                    </ToggleButtonGroup>
                                                    <Box
                                                        className={
                                                            classes.inlineActions
                                                        }
                                                    >
                                                        <Tooltip title="Move up">
                                                            <span>
                                                                <IconButton
                                                                    className={
                                                                        classes.railBtn
                                                                    }
                                                                    size="small"
                                                                    aria-label="Move up"
                                                                    disabled={
                                                                        !canUp
                                                                    }
                                                                    onClick={() =>
                                                                        handleReorder(
                                                                            field.field_id,
                                                                            'up',
                                                                        )
                                                                    }
                                                                >
                                                                    <ArrowUpwardIcon
                                                                        style={{
                                                                            fontSize: 18,
                                                                        }}
                                                                    />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title="Move down">
                                                            <span>
                                                                <IconButton
                                                                    className={
                                                                        classes.railBtn
                                                                    }
                                                                    size="small"
                                                                    aria-label="Move down"
                                                                    disabled={
                                                                        !canDown
                                                                    }
                                                                    onClick={() =>
                                                                        handleReorder(
                                                                            field.field_id,
                                                                            'down',
                                                                        )
                                                                    }
                                                                >
                                                                    <ArrowDownwardIcon
                                                                        style={{
                                                                            fontSize: 18,
                                                                        }}
                                                                    />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        {onMoveField &&
                                                        moveTargets.length >
                                                            0 &&
                                                        !policyLocked &&
                                                        !pinned ? (
                                                            <Tooltip title="Move to section">
                                                                <IconButton
                                                                    className={
                                                                        classes.railBtn
                                                                    }
                                                                    size="small"
                                                                    aria-label="Move to section"
                                                                    onClick={(
                                                                        e,
                                                                    ) =>
                                                                        setMoveMenuAnchor(
                                                                            {
                                                                                el:
                                                                                    e.currentTarget,
                                                                                fieldId:
                                                                                    field.field_id,
                                                                            },
                                                                        )
                                                                    }
                                                                >
                                                                    <SwapHorizIcon
                                                                        style={{
                                                                            fontSize: 18,
                                                                        }}
                                                                    />
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : null}
                                                        {!policyLocked &&
                                                        !pinned ? (
                                                            <Tooltip
                                                                title={
                                                                    field.show_if
                                                                        ? 'Edit contingency'
                                                                        : 'Add contingency'
                                                                }
                                                            >
                                                                <IconButton
                                                                    className={
                                                                        classes.railBtn
                                                                    }
                                                                    size="small"
                                                                    aria-label={
                                                                        field.show_if
                                                                            ? 'Edit contingency'
                                                                            : 'Add contingency'
                                                                    }
                                                                    onClick={(
                                                                        e,
                                                                    ) =>
                                                                        openContingencyEditor(
                                                                            e.currentTarget,
                                                                            field.field_id,
                                                                        )
                                                                    }
                                                                >
                                                                    <AccountTreeOutlinedIcon
                                                                        style={{
                                                                            fontSize: 18,
                                                                        }}
                                                                    />
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : null}
                                                        {policyLocked ? (
                                                            <Tooltip title="Controlled by Qualifying Methods — cannot remove">
                                                                <span>
                                                                    <IconButton
                                                                        className={
                                                                            classes.deleteBtn
                                                                        }
                                                                        size="small"
                                                                        aria-label="Remove from section"
                                                                        disabled
                                                                    >
                                                                        <DeleteOutlineIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        ) : pinned ? null : (
                                                            <Tooltip title="Remove from section">
                                                                <IconButton
                                                                    className={
                                                                        classes.deleteBtn
                                                                    }
                                                                    size="small"
                                                                    aria-label="Remove from section"
                                                                    onClick={() =>
                                                                        removeField(
                                                                            field.field_id,
                                                                        )
                                                                    }
                                                                >
                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </Box>

                                                <Box
                                                    className={
                                                        classes.mutedMeta
                                                    }
                                                >
                                                    {semanticsLocked ? (
                                                        <Typography
                                                            className={
                                                                classes.fieldIdMuted
                                                            }
                                                        >
                                                            {field.field_id}
                                                        </Typography>
                                                    ) : (
                                                        <TextField
                                                            className={
                                                                classes.fieldIdEdit
                                                            }
                                                            size="small"
                                                            variant="outlined"
                                                            value={
                                                                field.field_id
                                                            }
                                                            onChange={(e) =>
                                                                renameFieldId(
                                                                    field.field_id,
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            inputProps={{
                                                                'aria-label':
                                                                    'Field ID',
                                                                spellCheck: false,
                                                            }}
                                                        />
                                                    )}
                                                    {semanticsLocked ? (
                                                        <Chip
                                                            className={
                                                                classes.typeChip
                                                            }
                                                            size="small"
                                                            label={inputTypeLabel(
                                                                field.input_type,
                                                            )}
                                                        />
                                                    ) : (
                                                        <FormControl
                                                            className={
                                                                classes.typeControl
                                                            }
                                                            variant="outlined"
                                                            size="small"
                                                        >
                                                            <Select
                                                                value={
                                                                    field.input_type
                                                                }
                                                                displayEmpty
                                                                onChange={(e) =>
                                                                    handleCustomTypeChange(
                                                                        field.field_id,
                                                                        e.target
                                                                            .value as CustomFieldInputType,
                                                                    )
                                                                }
                                                            >
                                                                {CUSTOM_QUESTION_INPUT_TYPES.map(
                                                                    (opt) => (
                                                                        <MenuItem
                                                                            key={
                                                                                opt.value
                                                                            }
                                                                            value={
                                                                                opt.value
                                                                            }
                                                                        >
                                                                            {
                                                                                opt.label
                                                                            }
                                                                        </MenuItem>
                                                                    ),
                                                                )}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                </Box>

                                                {field.field_id ===
                                                GENDER_FIELD_ID ? (
                                                    <FormControl
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                    >
                                                        <InputLabel
                                                            id={`${field.field_id}-mode`}
                                                        >
                                                            How to ask
                                                        </InputLabel>
                                                        <Select
                                                            labelId={`${field.field_id}-mode`}
                                                            label="How to ask"
                                                            value={normalizeGenderQuestionMode(
                                                                field.gender_question_mode,
                                                            )}
                                                            onChange={(e) => {
                                                                const spec = genderQuestionSpec(
                                                                    e.target
                                                                        .value as GenderQuestionMode,
                                                                )
                                                                updateField(
                                                                    field.field_id,
                                                                    spec,
                                                                )
                                                            }}
                                                        >
                                                            <MenuItem value="id_marker">
                                                                As it appears on
                                                                a
                                                                government-issued
                                                                ID
                                                            </MenuItem>
                                                            <MenuItem value="self_id">
                                                                Gender identity
                                                            </MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                ) : null}

                                                {!semanticsLocked ? (
                                                    <TextField
                                                        size="small"
                                                        variant="outlined"
                                                        fullWidth
                                                        label="Question text"
                                                        value={
                                                            field.question_text
                                                        }
                                                        onChange={(e) =>
                                                            updateField(
                                                                field.field_id,
                                                                {
                                                                    question_text:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            )
                                                        }
                                                        inputProps={{
                                                            'aria-label':
                                                                'Question text',
                                                        }}
                                                    />
                                                ) : null}

                                                {showCustomOptions ? (
                                                    <TextField
                                                        label="Answer options (one per line)"
                                                        value={(
                                                            field.options || []
                                                        ).join('\n')}
                                                        onChange={(e) =>
                                                            updateField(
                                                                field.field_id,
                                                                {
                                                                    options: textToOptions(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                },
                                                            )
                                                        }
                                                        fullWidth
                                                        size="small"
                                                        variant="outlined"
                                                        multiline
                                                        rows={2}
                                                    />
                                                ) : null}
                                            </Box>
                                            <Box
                                                className={
                                                    classes.previewColumn
                                                }
                                            >
                                                <Typography
                                                    className={
                                                        classes.previewLabel
                                                    }
                                                >
                                                    User sees
                                                </Typography>
                                                <WorkbookFieldPreview
                                                    field={field}
                                                    required={field.required}
                                                    floorPlanOptions={
                                                        floorPlanOptions
                                                    }
                                                    premiumOptions={
                                                        premiumOptions
                                                    }
                                                    animalTypeOptions={
                                                        animalTypeOptions
                                                    }
                                                    dogBreedOptions={
                                                        dogBreedOptions
                                                    }
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            )
                        })}
                    </Box>

                    <Menu
                        anchorEl={moveMenuAnchor?.el ?? null}
                        open={Boolean(moveMenuAnchor)}
                        onClose={() => setMoveMenuAnchor(null)}
                        getContentAnchorEl={null}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        {moveTargets.map((target) => (
                            <MenuItem
                                key={target.bucket_id}
                                onClick={() => {
                                    if (!moveMenuAnchor) return
                                    handleMoveField(
                                        moveMenuAnchor.fieldId,
                                        target.bucket_id,
                                    )
                                    setMoveMenuAnchor(null)
                                }}
                            >
                                {target.label}
                            </MenuItem>
                        ))}
                    </Menu>

                    <Popover
                        open={Boolean(contingencyAnchor)}
                        anchorEl={contingencyAnchor?.el ?? null}
                        onClose={() => setContingencyAnchor(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <Box className={classes.contingencyPopover}>
                            <Typography className={classes.contingencyTitle}>
                                Contingency
                            </Typography>
                            <Typography className={classes.hint}>
                                Show this question only when another answer
                                matches. Optional / Required still applies when
                                it is shown.
                            </Typography>
                            <FormControl
                                variant="outlined"
                                size="small"
                                fullWidth
                            >
                                <InputLabel id="contingency-depends">
                                    Contingent on question
                                </InputLabel>
                                <Select
                                    labelId="contingency-depends"
                                    label="Contingent on question"
                                    value={contingencyDependsOn}
                                    onChange={(e) => {
                                        const id = e.target.value as string
                                        setContingencyDependsOn(id)
                                        const trigger = draft.fields.find(
                                            (f) => f.field_id === id,
                                        )
                                        const choices = trigger
                                            ? answerChoicesForTrigger(trigger)
                                            : []
                                        setContingencyEquals(choices[0] ?? '')
                                    }}
                                >
                                    {draft.fields
                                        .filter(
                                            (f) =>
                                                f.field_id !==
                                                    contingencyAnchor?.fieldId &&
                                                canBeContingencyTrigger(f),
                                        )
                                        .map((f) => (
                                            <MenuItem
                                                key={f.field_id}
                                                value={f.field_id}
                                            >
                                                {f.question_text}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                            <FormControl
                                variant="outlined"
                                size="small"
                                fullWidth
                                disabled={!contingencyDependsOn}
                            >
                                <InputLabel id="contingency-equals">
                                    When answer is
                                </InputLabel>
                                <Select
                                    labelId="contingency-equals"
                                    label="When answer is"
                                    value={contingencyEquals}
                                    onChange={(e) =>
                                        setContingencyEquals(
                                            e.target.value as string,
                                        )
                                    }
                                >
                                    {(contingencyDependsOn
                                        ? answerChoicesForTrigger(
                                              draft.fields.find(
                                                  (f) =>
                                                      f.field_id ===
                                                      contingencyDependsOn,
                                              ) ?? {
                                                  field_id: '',
                                                  question_text: '',
                                                  input_type: 'boolean',
                                                  enabled: true,
                                                  required: false,
                                              },
                                          )
                                        : []
                                    ).map((opt) => {
                                        const trigger = draft.fields.find(
                                            (f) =>
                                                f.field_id ===
                                                contingencyDependsOn,
                                        )
                                        return (
                                            <MenuItem key={opt} value={opt}>
                                                {displayAnswerForShowIf(
                                                    trigger,
                                                    opt,
                                                )}
                                            </MenuItem>
                                        )
                                    })}
                                </Select>
                            </FormControl>
                            <Box className={classes.actionsRow}>
                                {draft.fields.find(
                                    (f) =>
                                        f.field_id ===
                                        contingencyAnchor?.fieldId,
                                )?.show_if ? (
                                    <Button
                                        size="small"
                                        color="secondary"
                                        onClick={clearContingency}
                                    >
                                        Remove
                                    </Button>
                                ) : null}
                                <Box flex={1} />
                                <Button
                                    size="small"
                                    onClick={() => setContingencyAnchor(null)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="small"
                                    color="primary"
                                    variant="contained"
                                    disabled={
                                        !contingencyDependsOn ||
                                        !contingencyEquals
                                    }
                                    onClick={saveContingency}
                                >
                                    Save
                                </Button>
                            </Box>
                        </Box>
                    </Popover>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={() =>
                            onSave({
                                ...draft,
                                label: draft.label.trim() || title,
                                configured: true,
                                fields: draft.fields.map((f) => ({
                                    ...f,
                                    enabled: true,
                                })),
                            })
                        }
                    >
                        Save section
                    </Button>
                </DialogActions>
            </Dialog>
            <FhaAckDialog
                open={pendingGenderField != null}
                title="Add a gender question"
                body="Gender is a protected characteristic. Use this question for roommate assignment when the property does not allow coed rooms, not to deny housing or change terms."
                confirmLabel="Add question"
                onClose={() => setPendingGenderField(null)}
                onConfirm={() => {
                    if (!pendingGenderField) return
                    appendField(
                        orgFieldToSectionDraft(pendingGenderField, {
                            enabled: true,
                            required: true,
                        }),
                    )
                    setPendingGenderField(null)
                }}
            />
        </>
    )
}

// Re-export helper used by callers that still import from this module path.
export { getWorkbookSectionDraft }
